/**
 * R2 Advanced CA: Change Orders Section
 *
 * Displays change orders for a project with:
 * - Impact summary banner (approved total, revised contract value)
 * - Table with status badges and amounts
 * - Create / Edit modal (title, description, line items)
 * - Submit / Approve / Reject workflow buttons
 * - PDF download
 */
import React, { useState } from 'react';
import {
  useChangeOrders,
  useChangeOrderImpact,
  useCreateChangeOrder,
  useUpdateChangeOrder,
  useSubmitChangeOrder,
  useApproveChangeOrder,
  useRejectChangeOrder,
  type ChangeOrderItem,
} from '@/hooks/useChangeOrders';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  PlusCircle,
  Edit2,
  ChevronDown,
  ChevronUp,
  FileText,
  CheckCircle2,
  XCircle,
  Send,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { getAuth } from 'firebase/auth';

interface Props {
  projectId: string;
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  draft: 'outline',
  pending: 'secondary',
  approved: 'default',
  rejected: 'destructive',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? 'outline'}>
      {STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

function fmtCurrency(value: number | string | undefined | null): string {
  const num = Number(value ?? 0);
  return num.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });
}

// ─── Item row editor ─────────────────────────────────────────────────────────

interface ItemRowProps {
  item: ChangeOrderItem;
  index: number;
  onChange: (index: number, field: keyof ChangeOrderItem, value: string | number) => void;
  onRemove: (index: number) => void;
}

function ItemRow({ item, index, onChange, onRemove }: ItemRowProps) {
  const amount = (Number(item.quantity ?? 1) * Number(item.unit_price ?? 0)).toFixed(2);
  return (
    <tr className="border-b last:border-0">
      <td className="py-1 pr-2 w-full">
        <Input
          value={item.description}
          onChange={e => onChange(index, 'description', e.target.value)}
          placeholder="Description"
          className="h-7 text-sm"
        />
      </td>
      <td className="py-1 pr-2 w-20">
        <Input
          type="number"
          value={item.quantity}
          min={0.0001}
          step="any"
          onChange={e => onChange(index, 'quantity', parseFloat(e.target.value) || 1)}
          className="h-7 text-sm text-right"
        />
      </td>
      <td className="py-1 pr-2 w-28">
        <Input
          type="number"
          value={item.unit_price}
          step="0.01"
          onChange={e => onChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
          className="h-7 text-sm text-right"
        />
      </td>
      <td className="py-1 pr-2 w-24 text-right text-sm font-medium text-muted-foreground">
        ${amount}
      </td>
      <td className="py-1 w-10 text-center">
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-muted-foreground hover:text-destructive text-xs px-1"
        >
          ✕
        </button>
      </td>
    </tr>
  );
}

// ─── Create / Edit Modal ──────────────────────────────────────────────────────

interface ModalProps {
  projectId: string;
  existingCo?: any;
  onClose: () => void;
}

function ChangeOrderModal({ projectId, existingCo, onClose }: ModalProps) {
  const isEdit = !!existingCo;

  const [title, setTitle] = useState(existingCo?.title ?? '');
  const [description, setDescription] = useState(existingCo?.description ?? '');
  const [notes, setNotes] = useState(existingCo?.notes ?? '');
  const [items, setItems] = useState<ChangeOrderItem[]>(
    existingCo?.items?.length
      ? existingCo.items.map((i: any) => ({
          description: i.description,
          quantity: Number(i.quantity),
          unit_price: Number(i.unit_price),
          sort_order: i.sort_order ?? 0,
        }))
      : [{ description: '', quantity: 1, unit_price: 0 }]
  );

  const create = useCreateChangeOrder(projectId);
  const update = useUpdateChangeOrder(projectId, existingCo?.id ?? '');
  const isPending = create.isPending || update.isPending;

  function handleItemChange(index: number, field: keyof ChangeOrderItem, value: string | number) {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));
  }

  function addItem() {
    setItems(prev => [...prev, { description: '', quantity: 1, unit_price: 0 }]);
  }

  function removeItem(index: number) {
    setItems(prev => prev.filter((_, i) => i !== index));
  }

  const total = items.reduce((sum, it) => sum + Number(it.quantity ?? 1) * Number(it.unit_price ?? 0), 0);

  async function handleSave() {
    if (!title.trim()) return;
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      notes: notes.trim() || null,
      items: items.filter(it => it.description.trim()).map((it, idx) => ({
        ...it,
        sort_order: idx,
      })),
    };
    if (isEdit) {
      update.mutate(payload, { onSuccess: onClose });
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit CO-${String(existingCo.co_number).padStart(3, '0')}` : 'New Change Order'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="co-title">Title *</Label>
            <Input
              id="co-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Additional grading work"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="co-desc">Description</Label>
            <Textarea
              id="co-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Detailed description of scope change…"
              className="mt-1 min-h-[80px]"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Line Items</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addItem} className="h-6 text-xs">
                <PlusCircle className="mr-1 h-3 w-3" />
                Add item
              </Button>
            </div>
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium text-xs">Description</th>
                    <th className="px-2 py-1 text-right font-medium text-xs w-20">Qty</th>
                    <th className="px-2 py-1 text-right font-medium text-xs w-28">Unit Price</th>
                    <th className="px-2 py-1 text-right font-medium text-xs w-24">Amount</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="px-2">
                  {items.map((item, idx) => (
                    <ItemRow
                      key={idx}
                      item={item}
                      index={idx}
                      onChange={handleItemChange}
                      onRemove={removeItem}
                    />
                  ))}
                </tbody>
                <tfoot className="border-t bg-muted/30">
                  <tr>
                    <td colSpan={3} className="px-2 py-1 text-right text-xs font-medium text-muted-foreground">Total</td>
                    <td className="px-2 py-1 text-right text-sm font-semibold">${total.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div>
            <Label htmlFor="co-notes">Notes</Label>
            <Textarea
              id="co-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes or approval conditions…"
              className="mt-1 min-h-[60px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button onClick={handleSave} disabled={isPending || !title.trim()}>
            {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Change Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Review Notes Modal ───────────────────────────────────────────────────────

interface ReviewModalProps {
  coId: string;
  coNumber: number;
  action: 'approve' | 'reject';
  projectId: string;
  onClose: () => void;
}

function ReviewModal({ coId, coNumber, action, projectId, onClose }: ReviewModalProps) {
  const [notes, setNotes] = useState('');
  const approve = useApproveChangeOrder(projectId);
  const reject = useRejectChangeOrder(projectId);
  const isPending = approve.isPending || reject.isPending;

  function handleConfirm() {
    const payload = { coId, notes: notes.trim() || undefined };
    if (action === 'approve') {
      approve.mutate(payload, { onSuccess: onClose });
    } else {
      reject.mutate(payload, { onSuccess: onClose });
    }
  }

  const isApprove = action === 'approve';
  const title = isApprove
    ? `Approve CO-${String(coNumber).padStart(3, '0')}`
    : `Reject CO-${String(coNumber).padStart(3, '0')}`;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          {isApprove && (
            <p className="text-sm text-muted-foreground mb-3">
              Approving will add this CO's line items to the active SOV.
            </p>
          )}
          <Label htmlFor="review-notes">Notes (optional)</Label>
          <Textarea
            id="review-notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={isApprove ? 'Approval notes…' : 'Reason for rejection…'}
            className="mt-1 min-h-[80px]"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button
            variant={isApprove ? 'default' : 'destructive'}
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? 'Saving…' : isApprove ? 'Approve' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChangeOrdersSection({ projectId }: Props) {
  const { data: cosData, isLoading: cosLoading, isError: cosError } = useChangeOrders(projectId);
  const { data: impactData } = useChangeOrderImpact(projectId);

  const submit = useSubmitChangeOrder(projectId);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editCo, setEditCo] = useState<any | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [reviewModal, setReviewModal] = useState<{ coId: string; coNumber: number; action: 'approve' | 'reject' } | null>(null);

  const cos: any[] = (cosData as any)?.change_orders ?? [];
  const impact = (impactData as any)?.impact;

  function toggleExpand(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleDownloadPdf(coId: string, coNumber: number, title: string) {
    try {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();
      const resp = await fetch(api.changeOrders.pdfUrl(coId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error('PDF generation failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CO-${String(coNumber).padStart(3, '0')} - ${title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  }

  if (cosLoading) {
    return <div className="py-6 text-center text-sm text-muted-foreground">Loading change orders…</div>;
  }

  return (
    <div className="space-y-4 pt-4 border-t mt-6">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Change Orders</h3>
          <p className="text-sm text-muted-foreground">
            Track and approve scope changes. Approved COs adjust the SOV automatically.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <PlusCircle className="mr-1 h-3 w-3" />
          New CO
        </Button>
      </div>

      {/* Impact summary banner */}
      {impact && (impact.total_cos > 0 || impact.original_contract_value > 0) && (
        <div className="flex flex-wrap gap-4 rounded-md border bg-muted/30 px-4 py-3 text-sm">
          <div className="flex items-center gap-1.5">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Original contract:</span>
            <span className="font-semibold">{fmtCurrency(impact.original_contract_value)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Approved COs ({impact.approved_cos}):</span>
            <span className={`font-semibold ${Number(impact.total_approved_amount) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              +{fmtCurrency(impact.total_approved_amount)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-muted-foreground">Revised contract:</span>
            <span className="font-bold text-base">{fmtCurrency(impact.revised_contract_value)}</span>
          </div>
          {impact.pending_cos > 0 && (
            <div className="flex items-center gap-1 text-amber-700">
              <AlertCircle className="h-3.5 w-3.5" />
              <span>{impact.pending_cos} pending</span>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {cosError && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Failed to load change orders. Please refresh.
        </div>
      )}

      {/* Empty state */}
      {!cosLoading && !cosError && cos.length === 0 && (
        <div className="rounded-md border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground mb-3">No change orders yet.</p>
          <Button size="sm" variant="outline" onClick={() => setShowCreateModal(true)}>
            <PlusCircle className="mr-1 h-3 w-3" />
            Create first CO
          </Button>
        </div>
      )}

      {/* CO table */}
      {cos.length > 0 && (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium w-16">CO #</th>
                <th className="px-3 py-2 text-left font-medium">Title</th>
                <th className="px-3 py-2 text-left font-medium w-24">Status</th>
                <th className="px-3 py-2 text-right font-medium w-28">Amount</th>
                <th className="px-3 py-2 text-right font-medium w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cos.map((co: any) => {
                const isExpanded = expandedIds.has(co.id);
                return (
                  <React.Fragment key={co.id}>
                    <tr className="border-t hover:bg-muted/30">
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        CO-{String(co.co_number).padStart(3, '0')}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          className="text-left hover:underline font-medium"
                          onClick={() => toggleExpand(co.id)}
                        >
                          {co.title}
                        </button>
                        {co.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{co.description}</p>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={co.status} />
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        {fmtCurrency(co.amount)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Draft actions */}
                          {co.status === 'draft' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs"
                                onClick={() => setEditCo(co)}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                onClick={() => submit.mutate(co.id)}
                                disabled={submit.isPending}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Submit
                              </Button>
                            </>
                          )}

                          {/* Pending actions */}
                          {co.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => setReviewModal({ coId: co.id, coNumber: co.co_number, action: 'approve' })}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-red-300 text-red-700 hover:bg-red-50"
                                onClick={() => setReviewModal({ coId: co.id, coNumber: co.co_number, action: 'reject' })}
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}

                          {/* PDF download */}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            title="Download PDF"
                            onClick={() => handleDownloadPdf(co.id, co.co_number, co.title)}
                          >
                            <FileText className="h-3 w-3" />
                          </Button>

                          {/* Expand toggle */}
                          <button
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => toggleExpand(co.id)}
                          >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr className="border-t bg-muted/10">
                        <td colSpan={5} className="px-4 py-3">
                          {(co.items ?? []).length > 0 ? (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-muted-foreground">
                                  <th className="text-left pb-1 font-medium">Description</th>
                                  <th className="text-right pb-1 font-medium w-16">Qty</th>
                                  <th className="text-right pb-1 font-medium w-24">Unit Price</th>
                                  <th className="text-right pb-1 font-medium w-24">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {co.items.map((item: any, idx: number) => (
                                  <tr key={idx} className="border-t border-muted">
                                    <td className="py-1">{item.description}</td>
                                    <td className="py-1 text-right">{item.quantity}</td>
                                    <td className="py-1 text-right">{fmtCurrency(item.unit_price)}</td>
                                    <td className="py-1 text-right font-medium">{fmtCurrency(item.amount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-xs text-muted-foreground">No line items.</p>
                          )}
                          {co.notes && (
                            <p className="text-xs text-muted-foreground mt-2 border-t pt-2">
                              <span className="font-medium">Notes:</span> {co.notes}
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <ChangeOrderModal
          projectId={projectId}
          onClose={() => setShowCreateModal(false)}
        />
      )}
      {editCo && (
        <ChangeOrderModal
          projectId={projectId}
          existingCo={editCo}
          onClose={() => setEditCo(null)}
        />
      )}
      {reviewModal && (
        <ReviewModal
          coId={reviewModal.coId}
          coNumber={reviewModal.coNumber}
          action={reviewModal.action}
          projectId={projectId}
          onClose={() => setReviewModal(null)}
        />
      )}
    </div>
  );
}
